// src/components/GhostProfile.js - Updated with proper array-based attempt tracking

import React, { useState, useEffect } from 'react';
import { Ghost } from 'lucide-react';
import styles from './GhostProfile.module.css';

/**
 * Renders the ghost mode password prompt with improved security
 * - Tracks attempts in the database rather than component state
 * - Implements IP-based rate limiting
 * - Uses a timed lockout mechanism
 *
 * @param {string} username - Used to display linkist.se/{username}
 * @param {function} onPasswordSubmit - async callback; must return { success: true } or { success: false }
 * @param {string} customMessage - optional: "This profile is locked!"
 * @param {object} securityData - Data about attempt limits: { remainingAttempts, isLocked, lockExpiry }
 * @param {function} onAttemptTracking - Function to track a new attempt in the database
 */
const GhostProfile = ({ 
  username, 
  onPasswordSubmit, 
  customMessage,
  securityData = {},
  onAttemptTracking
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [localLocked, setLocalLocked] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // Extract security data with defaults
  const {
    remainingAttempts = 4,
    isLocked = false, 
    lockExpiry = null
  } = securityData;

  // Calculate and update time remaining in lockout if locked
  useEffect(() => {
    if (!isLocked || !lockExpiry) return;
    
    const updateTimeRemaining = () => {
      const now = new Date();
      const expiryTime = new Date(lockExpiry);
      const diffMs = expiryTime - now;
      
      if (diffMs <= 0) {
        setTimeRemaining(null);
        return;
      }
      
      // Calculate minutes and seconds remaining
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setTimeRemaining(`${minutes}m ${seconds}s`);
    };
    
    // Update immediately
    updateTimeRemaining();
    
    // Set up interval to update every second
    const interval = setInterval(updateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [isLocked, lockExpiry]);

  // Default fallback message if none is provided
  const defaultMessage = "this Linkist profile is in ghost mode";
  const displayMessage = customMessage || defaultMessage;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't allow submission if locked
    if (isLocked || localLocked) return;

    try {
      // The parent callback should return { success: true } if correct, else { success: false }
      const result = await onPasswordSubmit(password);
      
      if (!result.success) {
        // If server says we're now locked, update the UI
        if (result.isLocked) {
          setLocalLocked(true);
          setError('Too many attempts. Please try again later.');
        } else {
          setError(`Incorrect password. You have ${result.remainingAttempts} attempts remaining.`);
        }
        
        setPassword('');
      }
    } catch (err) {
      console.error("GhostProfile error:", err);
      setError('An error occurred. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.ghostProfile}>
        <div className={styles.header}>
          <Ghost size={60} className={styles.ghostIcon} />
          <h1>{displayMessage}</h1>
          <br />
          <p className={styles.username}>linkist.se/{username}</p>
        </div>

        {!isLocked && !localLocked ? (
          <form onSubmit={handleSubmit} className={styles.passwordForm}>
            <p className={styles.description}>
              enter the 6-character password to view this profile
            </p>

            <div className={styles.inputWrapper}>
              <input
                type="text"
                value={password}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  // Only A-Z0-9, up to 6 chars
                  if (val.length <= 6 && /^[A-Z0-9]*$/.test(val)) {
                    setPassword(val);
                    setError('');
                  }
                }}
                placeholder="XXXXXX"
                maxLength={6}
                className={styles.passwordInput}
                disabled={isLocked || localLocked}
              />

              {error && <div className={styles.error}>{error}</div>}
              <div className={styles.attempts}>
                attempts remaining: {remainingAttempts}
              </div>
            </div>

            <button
              type="submit"
              disabled={password.length !== 6 || isLocked || localLocked}
              className={styles.submitButton}
            >
              view profile
            </button>
          </form>
        ) : (
          <div className={styles.lockedMessage}>
            <p>Too many incorrect attempts.</p>
            {timeRemaining ? (
              <p>Try again in: {timeRemaining}</p>
            ) : (
              <p>Please try again later.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GhostProfile;